<script lang="ts" setup>
import { computed } from 'vue';
import { UiAvatar } from '../ui-avatar';

interface Props {
  names: string[];
  maxVisible?: number;
}

const props = withDefaults(defineProps<Props>(), {
  maxVisible: 4,
});

const colors = ['#18A582', '#A078A9', '#4D98B4', '#EE4E36', '#53C279', '#F8C136'];

const hasOverflow = computed(() => props.names.length > props.maxVisible);

const visibleNames = computed(() => {
  const limit = hasOverflow.value ? props.maxVisible - 1 : props.maxVisible;
  return props.names.slice(0, limit);
});

const remainingNames = computed(() => {
  if (!hasOverflow.value) {
    return [];
  }

  return props.names.slice(visibleNames.value.length);
});

const remaining = computed(() => {
  if (!hasOverflow.value) {
    return '';
  }

  const remainingCount = props.names.length - visibleNames.value.length;

  return remainingCount > 9 ? `9+` : `+${remainingCount}`;
});
</script>

<template>
  <div data-ui-component="UiAvatars" :class="$style.root">
    <div v-for="(name, index) in visibleNames" :key="name" :class="$style.avatar">
      <slot v-bind="{ name, backgroundColor: colors[index % colors.length] }">
        <UiAvatar :name :background-color="colors[index % colors.length]" />
      </slot>
    </div>
    <div v-if="remaining" :class="$style.count">
      <slot
        name="count"
        v-bind="{
          count: remaining,
          names: remainingNames,
          backgroundColor: '#D1D5DB',
          textColor: '#978f91',
        }"
      >
        <UiAvatar background-color="#D1D5DB" text-color="#978f91">
          {{ remaining }}
        </UiAvatar>
      </slot>
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  display: inline-flex;
  align-items: center;
}

.avatar {
  display: inline-flex;
  border-radius: 50%;
  border: 1px solid #f0f1f4;

  &:not(:first-child) {
    margin-left: -4px;
  }
}

.count {
  margin-left: 4px;
  font-size: 11px;
  color: #978f91;
}
</style>
